import React, { FC } from 'react';

import Container from '@v2/shared/components/Container';
import FooterLink from './FooterLink';
import useCurrentYear from '@v2/shared/lib/useCurrentYear';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { openConsentPreferences, useConsentLawActive } from '@v2/shared/lib/cookieYes';

type Props = {
  /**
   * The year the server rendered, handed down from `FullLayoutProps['copyrightYear']`.
   * Absent on the legacy `layouts/full/footer.html` mount, which passes no layout props
   * and is client-rendered — the hook reads the clock instead.
   */
  year?: number;
};

/**
 * The bottom bar: the copyright line, and the legal links that used to sit in the About
 * Us column. They are here because that is where a visitor looks for them — and because
 * the columns above are now about shopping, helping and the company, none of which is
 * where a privacy policy belongs.
 *
 * The Do Not Sell link comes with them, and it is the one with rules attached. Cal. Civ.
 * Code 1798.135(a)(1) fixes its title, and it may only be shown where a state opt-out law
 * applies — CookieYes is what knows, which is why it is gated on `useConsentLawActive`
 * rather than on the site's country: this footer is also DiscountMags.ca's and AU's, and
 * a Californian shopping the AU storefront is still covered. `openConsentPreferences`
 * opens the preference centre and stops the browser following the href; the href is the
 * fallback for when CookieYes cannot be asked. `DoNotSellFooterLinkTest` pins all of it.
 *
 * The links are `FooterLink`s rather than a local variant, so the 44px floor is described
 * once. The bar only needs a different hover colour, and hover colour does not collide
 * with anything FooterLink sets, so clsx has nothing to arbitrate.
 */
const BottomBar: FC<Props> = ({ year: initialYear }) => {
  const year = useCurrentYear(initialYear);
  const { buildUrl } = useUrlGenerator();
  const consentLawActive = useConsentLawActive();

  return (
    <div className="bg-slate-800 flex justify-center py-2 text-slate-300 text-sm">
      <Container className="flex flex-col items-center gap-x-6 text-center lg:flex-row lg:justify-between lg:text-left">
        {/* `py-2` and not the 44px floor the links carry: this is a line of text, not a
            target, and giving it the tap-target class would read as though it were one. */}
        <span className="flex items-center whitespace-nowrap py-2">
          &copy; {year} DiscountMags.com
          <span className="text-slate-400 mx-2">&bull;</span> All rights reserved.
        </span>
        {/* `lg:flex-wrap` with `whitespace-nowrap` on each link, so the row breaks
            BETWEEN links and never inside one. Without it the five items are squeezed
            instead: measured at a 1024 and a 1100px viewport with the consent link shown —
            which is the normal state for the visitors it exists for — four of the five
            wrapped, rendering the statutory title as "Do Not Sell or Share My Personal /
            Information". It fits on one line from 1280px up. */}
        <div className="flex flex-col items-center lg:flex-row lg:flex-wrap lg:justify-end lg:gap-x-6">
          <FooterLink className="whitespace-nowrap" href={buildUrl('/privacy')}>Privacy Policy</FooterLink>
          {consentLawActive && (
            // Lands on the section of the policy describing the choice, so it is next to
            // the policy it belongs to rather than at the end of the row, where the
            // longest label would also be the least conspicuous.
            <FooterLink className="whitespace-nowrap" href={buildUrl('/privacy#your-choices')} onClick={openConsentPreferences}>
              Do Not Sell or Share My Personal Information
            </FooterLink>
          )}
          <FooterLink className="whitespace-nowrap" href={buildUrl('/terms')}>Terms of Use</FooterLink>
          <FooterLink className="whitespace-nowrap" href={buildUrl('/copyright')}>Copyright &amp; Notices</FooterLink>
          <FooterLink className="whitespace-nowrap" href={buildUrl('/accessibility')}>Accessibility</FooterLink>
        </div>
      </Container>
    </div>
  );
};

export default BottomBar;
